import React, { FunctionComponent, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  DialogContent,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Toolbar,
  Tooltip,
  useTheme
} from "@mui/material";
import { strings } from "../../../localization/LocalizedStrings";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { arrayMoveMutable } from "array-move";
import { ICollection, useDeleteCollection, useGetCollections } from "../../../hooks/collections";
import { CollectionItem } from "../CollectionItem";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { AlertDialog } from "../../AlertDialog";
import { useIsMounted } from "../../../hooks/isMounted";
import { StringFormat } from "../../../models/StringFormat";
import { ModifyCollectionDialog } from "../ModifyCollectionDialog";
import styles from "./styles.module.scss";
import { FilterElement } from "../../../models/FilterElement";
import { useAssignToCollectionByFilter } from "../../../hooks/albums/useAssignToCollectionByFilter";
import { toast } from "react-toastify";
import { useGetStandardCollections } from "../../../hooks/collections/useGetStandardCollections";
import { useReorderCollections } from "../../../hooks/collections/useReorderCollections";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { useShareLink } from "../../../hooks/share/useShareLink";
import { SearchBar } from "../../SearchBar";
import { RoutesBuilder } from "../../../models/RoutesBuilder";
import { ContentUnavailableNotice } from "../../attributes/ContentUnavailableNotice";
import { useContextMenu } from "../../../hooks/contextMenu";
import { productIconMap } from "../../../models/ProductUtilities";
import { ProductType } from "../../../hooks/products/interfaces";
import { PiperDialog } from "../../PiperDialog";
import { Icon, IconType } from "../../../icons";
import { useCurrentPrivateCollection } from "../../../hooks/collections/useCollectionManager";
import { useAlbumFilterManager } from "../../../hooks/albums/useAlbumFilterManager";
import { IIsNegatedAlbumsParameters } from "../../../hooks/albums";
import { useResettableDialog } from "../../../hooks/resettableDialog/useResettableDialog";

export const CollectionsDialog: FunctionComponent<ICollectionsDialogProps> = ({
  open,
  setOpen,
  isLayout,
  canModifyCollections,
  displayStandardCollections,
  onCollectionSelected,
  selectedCollections,
  forceLoading,
  canNegate
}) => {
  const theme = useTheme();
  const isMounted = useIsMounted();
  const isMobile = useIsMobile();
  const [currentPrivateCollection] = useCurrentPrivateCollection.useState();
  const { reorderCollections } = useReorderCollections();
  const { assignToCollectionByFilter } = useAssignToCollectionByFilter();
  const { getCurrentFilterElements, getCurrentIsNegated } = useAlbumFilterManager(isLayout);
  const [isNegated, setIsNegated] = useState<IIsNegatedAlbumsParameters>(getCurrentIsNegated());
  const [loading, setLoading] = useState(true);
  const [loadingStandardCollections, setLoadingStandardCollections] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isAssignAlertOpen, setIsAssignAlertOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const standardCollections = useGetStandardCollections(
    setLoadingStandardCollections,
    isMounted.current,
    [open]
  );
  const [collections, resetCollections] = useGetCollections(setLoading);
  const [searchText, setSearchText] = useState("");
  const { deleteCollection } = useDeleteCollection();
  const {
    state: modifyDialogState,
    key: modifyDialogKey,
    openAdd,
    openEdit,
    close: closeModifyDialog
  } = useResettableDialog<ICollection>();
  const [contextCollection, setContextCollection] = useState<ICollection>();
  const [shareLinkComponents, fetchShareLink, , isShareLoading] = useShareLink(
    contextCollection?.name
  );
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const onClose = () => {
    setOpen(false);
    setSearchText("");
  };
  const onError = (body: any) => {
    if (isMounted.current) {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
  };
  const performDeletion = async (collection: ICollection) => {
    setLoading(true);
    const { status, body } = await deleteCollection(collection);
    setLoading(false);
    if (StatusCodesHelper.isSuccessful(status)) {
      resetCollections();
      toast.success(strings.generic_update_success);
    } else {
      onError(body);
    }
  };
  const assignByFilter = async (collection: ICollection) => {
    if (!getCurrentFilterElements()) return;
    const request = assignToCollectionByFilter(
      {
        assignToCollectionId: collection.uuid,
        collectionId: currentPrivateCollection?.uuid,
        isNegated: isNegated
      },
      getCurrentFilterElements()
    );
    if (!request) return;
    setLoading(true);
    const { status, body } = await request;
    setLoading(false);
    if (StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(strings.collection_assign_filtered_albums_success);
    } else {
      setAlertMessage(body.message);
    }
    setIsAlertOpen(true);
  };
  const filteredCollections = useMemo(() => {
    if (searchText == "") {
      return collections;
    }
    return collections?.filter(collection => {
      return (
        collection.name.toLowerCase().includes(searchText.toLowerCase()) ||
        collection.description?.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [collections, searchText]);
  const searchBar = () => (
    <Stack direction={"row"} alignItems={"center"}>
      {canNegate && (
        <Button
          onClick={() =>
            setIsNegated(isNegated => ({ ...isNegated, collectionId: !isNegated.collectionId }))
          }
          sx={{
            fontSize: "15px",
            fontWeight: "normal",
            padding: 0,
            borderWidth: 1,
            borderColor: theme.palette.colors?.border,
            borderStyle: "solid",
            borderRadius: "12px",
            color: isNegated.collectionId ? theme.palette.colors.remove : undefined,
            height: "40px !important"
          }}
        >
          {isNegated.collectionId ? strings.filter_is_not : strings.filter_is}
        </Button>
      )}
      <SearchBar
        className={styles.searchBar}
        onSearchRequested={setSearchText}
        onSearchDismissed={() => setSearchText("")}
        timed={false}
        minimumSearchLength={0}
        autoFocus={!isMobile}
      />
    </Stack>
  );
  const skeletons = () =>
    Array.from({ length: 5 }, (_value, index) => (
      <div key={`div_${index}`} className={styles.cardContainer}>
        <Skeleton
          key={index}
          variant={"rectangular"}
          height={124}
          className={styles.collectionCardSkeleton}
        />
      </div>
    ));
  return (
    <PiperDialog
      fullWidth
      maxWidth={"xs"}
      open={open}
      onClose={onClose}
      PaperProps={{ style: { height: "100%" } }}
    >
      <Toolbar style={{ minHeight: "unset" }}>
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <h3 style={{ marginRight: 8 }}>{strings.collections_title}</h3>
            <ToolbarButton
              onClick={openAdd}
              tooltip={strings.add_collection}
              iconType={IconType.AddCircle}
            />
            <Box flexGrow={1} />
            <ToolbarButton
              href={`${window.location.protocol}//${
                window.location.host
              }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.collections.home()}`}
              target={"_blank"}
              tooltip={strings.help}
              iconType={IconType.Help}
            />
            <ToolbarButton
              loading={loading || isShareLoading || loadingStandardCollections}
              onClick={onClose}
              tooltip={strings.close}
              iconType={IconType.Close}
            />
          </div>
          {searchBar()}
        </div>
      </Toolbar>
      <Card className={styles.contentCard}>
        <DragDropContext
          onDragEnd={async result => {
            if (!collections || searchText != "") return;
            if (!result.destination || result.source.index == result.destination.index) {
              return;
            }
            arrayMoveMutable(collections, result.source.index, result.destination.index);
            setLoading(true);
            const { status } = await reorderCollections(collections);
            if (StatusCodesHelper.isSuccessful(status)) {
              toast.success(strings.generic_update_success);
            } else {
              toast.error(strings.generic_update_error);
            }
            setLoading(false);
          }}
        >
          <DialogContent
            sx={{
              padding: isMobile ? 0 : 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 450
            }}
          >
            {displayStandardCollections &&
              !forceLoading &&
              standardCollections?.map(collection => (
                <CollectionItem
                  key={`ci_${collection.uuid}`}
                  cardClassName={styles.collectionCard}
                  collection={collection}
                  canMove={false}
                  selected={selectedCollections.includes(collection.uuid)}
                  onSelection={() => {
                    if (onCollectionSelected) {
                      onCollectionSelected(collection, isNegated);
                      onClose();
                    }
                  }}
                />
              ))}
            <Droppable key={"droppable"} droppableId={"collection"} isDropDisabled={loading}>
              {provided => (
                <div
                  key={"div_droppable"}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    flexGrow: filteredCollections?.length == 0 ? 1 : 0
                  }}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {filteredCollections && !loading && !forceLoading
                    ? filteredCollections.map((collection, index) => (
                        <Draggable
                          key={collection.uuid}
                          draggableId={collection.uuid}
                          index={index}
                          isDragDisabled={searchText != ""}
                        >
                          {provided => (
                            <div
                              key={`div_${collection.uuid}`}
                              className={styles.cardContainer}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <CollectionItem
                                key={`ci_${collection.uuid}`}
                                cardClassName={styles.collectionCard}
                                collection={collection}
                                selected={selectedCollections.includes(collection.uuid)}
                                canMove={true}
                                onContextMenu={event => {
                                  setContextCollection(collection);
                                  handleContextMenu(event);
                                }}
                                onSelection={() => {
                                  if (canModifyCollections) {
                                    openEdit(collection);
                                  } else {
                                    if (onCollectionSelected) {
                                      onCollectionSelected(collection, isNegated);
                                      onClose();
                                    }
                                  }
                                }}
                              >
                                <Stack
                                  className={styles.hoverButtonsStack}
                                  direction={"row"}
                                  spacing={1}
                                >
                                  <Tooltip title={strings.options} arrow disableInteractive>
                                    <IconButton
                                      className={styles.hoverButton}
                                      style={{
                                        backgroundColor: theme.palette.colors?.counterBackground
                                      }}
                                      onClick={event => {
                                        event.stopPropagation();
                                        setContextCollection(collection);
                                        handleContextMenu(event);
                                      }}
                                    >
                                      <Icon type={IconType.MoreVertical} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </CollectionItem>
                            </div>
                          )}
                        </Draggable>
                      ))
                    : skeletons()}
                  {loading && skeletons()}
                  <ContentUnavailableNotice
                    isLoading={loading || forceLoading}
                    items={filteredCollections}
                    emptyIconType={productIconMap[ProductType.COLLECTIONS]}
                    emptyTitle={strings.empty_collections}
                    emptyDescription={strings.empty_attribute_description}
                    addButtonTitle={strings.add_collection}
                    onAddItem={openAdd}
                    searchText={searchText}
                    canAdd
                  />
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DialogContent>
        </DragDropContext>
      </Card>
      <Menu {...menuProps}>
        {contextCollection?.visibility == "public" && (
          <MenuItem
            onClick={async () => {
              handleMenuClose();
              await fetchShareLink(contextCollection?.uuid, undefined);
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.Share} />
            </ListItemIcon>
            {strings.share_collection}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextCollection);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.collection_modify}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setIsAssignAlertOpen(true);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.PlaylistAdd} />
          </ListItemIcon>
          {strings.collection_assign_filtered_albums}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setIsDeleteAlertOpen(true);
          }}
          style={{ color: theme.palette.colors?.remove }}
          dense
        >
          <ListItemIcon>
            <Icon style={{ color: theme.palette.colors?.remove }} type={IconType.DeleteForever} />
          </ListItemIcon>
          {strings.delete}
        </MenuItem>
      </Menu>
      <ModifyCollectionDialog
        key={modifyDialogKey}
        open={modifyDialogState.mode !== "closed"}
        onClose={closeModifyDialog}
        collection={modifyDialogState.item}
        callback={resetCollections}
        onDelete={() => {
          setIsDeleteAlertOpen(true);
          setContextCollection(modifyDialogState.item);
        }}
      />
      {shareLinkComponents()}
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
      <AlertDialog
        isConfirm={true}
        message={StringFormat(
          strings.collection_delete_confirmation,
          contextCollection?.name ?? ""
        )}
        open={isDeleteAlertOpen}
        setOpen={setIsDeleteAlertOpen}
        onConfirm={async () => {
          closeModifyDialog();
          await performDeletion(contextCollection);
        }}
      />
      <AlertDialog
        isConfirm={true}
        message={strings.collection_assign_filtered_albums_confirm}
        open={isAssignAlertOpen}
        setOpen={setIsAssignAlertOpen}
        onConfirm={async () => {
          if (!contextCollection) return;
          await assignByFilter(contextCollection);
        }}
      />
    </PiperDialog>
  );
};

export interface ICollectionsDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  isLayout: boolean;
  canModifyCollections: boolean;
  displayStandardCollections: boolean;
  filterElements?: FilterElement[];
  onCollectionSelected?: (collection: ICollection, isNegated: IIsNegatedAlbumsParameters) => void;
  selectedCollections: string[];
  forceLoading: boolean;
  canNegate: boolean;
}
